import { useState } from "react";
import { Waves, Landmark, Sun, Bike, MapPin, ExternalLink, Navigation } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useInView } from "@/hooks/useInView";

const ICON_MAP = { waves: Waves, landmark: Landmark, sun: Sun, bike: Bike };

// MAPA DE FOTOS (Pontos de Interesse)
const PHOTO_MAP = {
  "Nazaré": "/nazare 1.jpg",
  "Alcobaça": "/Alcobaça-mosteiro.jpg",
  "Praias": "/paredes.jpg",      
  "Beaches": "/paredes.jpg",     
  "Atividades": "/atividades.jpg"
};

export default function Location() {
  const { t, lang } = useLanguage();
  const [ref, inView] = useInView();
  const [activePoi, setActivePoi] = useState(0);
  
  const pois = t("location.pois", { returnObjects: true });
  const BEACH_GUIDE_URL = "https://beach.yourholiday.pt/best-beaches-silver-coast/";
  
  // Link direto para abrir no Google Maps/GPS do telemóvel
  const DIRECTIONS_URL = "https://maps.google.com/?cid=11895573319077550854&g_mp=Cidnb29nbGUubWFwcy5wbGFjZXMudjEuUGxhY2VzLlNlYXJjaFRleHQ";

  return (
    <section id="location" className="py-24 md:py-32 bg-[#FDFCF8]" data-testid="location-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div
          ref={ref}
          className={`transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          {/* Badge e Título */}
          <div className="flex items-center gap-3 mb-6">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#3A4D39] font-manrope">
              {t("location.badge")}
            </span>
            <div className="flex-1 h-px bg-[#3A4D39]/20" />
          </div>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-14">
            <div>
              <h2 className="font-playfair text-4xl md:text-5xl font-normal text-[#1F2937] leading-tight">
                {t("location.title")}
              </h2>
              <p className="text-base font-manrope font-light text-[#6B7280] mt-3 max-w-xl">
                {t("location.subtitle")}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            
            {/* NOVO BLOCO ESQUERDO: Card de Direções + Foto do Roteiro */}
            <div className="flex flex-col gap-6">
              
              {/* Card Premium Quinta das Cantigas */}
              <div className="bg-white p-8 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.08)] border border-stone-100">
                <div className="flex flex-col gap-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-[#3A4D39] p-3 rounded-2xl shadow-lg">
                      <MapPin className="text-white w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="font-playfair text-2xl text-[#1F2937] font-semibold">Quinta das Cantigas</h4>
                      <p className="text-sm font-manrope font-medium text-[#3A4D39] mt-0.5">Eco Glamping Resort</p>
                    </div>
                  </div>
                  
                  <div className="py-4 border-y border-stone-100">
                    <p className="text-sm font-manrope text-[#6B7280] leading-relaxed">
                      Travessa da Lagoa, Lagoa do Cão<br />
                      2460-613 Alcobaça, Portugal
                    </p>
                  </div>

                  <a 
                    href={DIRECTIONS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-3 py-4 px-6 bg-[#3A4D39] hover:bg-[#2A3829] text-white font-bold text-xs uppercase tracking-[0.15em] rounded-2xl transition-all shadow-xl active:scale-95 group"
                  >
                    <Navigation className="w-5 h-5 group-hover:animate-pulse" />
                    {lang === "pt" ? "Como Chegar / Abrir GPS" : "Get Directions / Open GPS"}
                  </a>
                </div>
              </div>

              {/* Foto do Roteiro (loc1.jpg) */}
              <div className="rounded-3xl overflow-hidden shadow-lg border border-stone-100 bg-white p-2">
                <div className="relative group">
                  <img 
                    src="/loc1.jpg" 
                    alt="Roteiro Quinta das Cantigas" 
                    className="w-full h-auto rounded-2xl object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                  />
                  <div className="absolute inset-0 bg-black/5 rounded-2xl pointer-events-none" />
                </div>
                <p className="text-[10px] text-center text-[#6B7280] uppercase tracking-widest font-bold py-3">
                  {lang === "pt" ? "Mapa de Acesso Sugerido" : "Suggested Access Route"}
                </p>
              </div>
            </div>

            {/* Lista de Pontos de Interesse (POIs) - Mantida à Direita */}
            <div className="flex flex-col gap-3">
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#6B7280] mb-2 font-manrope">
                {t("location.discoverLabel")}
              </p>

              {Array.isArray(pois) && pois.map((poi, i) => {
                const Icon = ICON_MAP[poi.icon] || MapPin;
                const isActive = activePoi === i;
                const displayImage = PHOTO_MAP[poi.name] || poi.image;

                const isBeachRelated = 
                  poi.icon === "waves" || 
                  poi.name.toLowerCase().includes("praia") || 
                  poi.name.toLowerCase().includes("beach");

                return (
                  <div
                    key={poi.name}
                    className={`group rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 ${
                      isActive ? "bg-[#3A4D39] shadow-lg translate-x-1" : "bg-white border border-stone-100 hover:border-[#3A4D39]/30"
                    }`}
                    onClick={() => setActivePoi(i)}
                  >
                    <div className="flex flex-col">
                      {isActive && displayImage && (
                        <div className="w-full h-48 overflow-hidden bg-stone-200">
                          <img 
                            src={displayImage} 
                            alt={poi.name} 
                            className="w-full h-full object-cover animate-in fade-in zoom-in-105 duration-700" 
                          />
                        </div>
                      )}
                      <div className="flex gap-4 p-5">
                        <div className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${isActive ? "bg-white/20" : "bg-[#3A4D39]/10"}`}>
                          <Icon className={`w-5 h-5 ${isActive ? "text-white" : "text-[#3A4D39]"}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <h3 className={`font-playfair text-lg font-medium ${isActive ? "text-white" : "text-[#1F2937]"}`}>
                              {poi.name}
                            </h3>
                            <span className={`text-xs font-manrope font-medium ${isActive ? "text-[#D4A373]" : "text-[#6B7280]"}`}>
                              {poi.distance}
                            </span>
                          </div>
                          <p className={`text-sm font-manrope font-light leading-relaxed mt-1 ${isActive ? "text-white/80" : "text-[#6B7280] truncate"}`}>
                            {poi.desc}
                          </p>
                          
                          {isActive && isBeachRelated && (
                            <a
                              href={BEACH_GUIDE_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#D4A373] hover:bg-white hover:text-[#3A4D39] text-white text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all mt-4 shadow-lg"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                              {lang === "pt" ? "Ver Guia de Praias" : "View Beach Guide"}
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
